import 'rxjs/Rx';
import 'rxjs/add/operator/map';
import {Observable} from 'rxjs';
import {LocationStrategy} from '@angular/common';
import {Injectable, Self} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';

import {BasicDataService} from "./basicData.service";
import {LoggerService} from "../logger.service";
import {
    AuthorEntirePollResponse,
    AuthorPollResponse,
    BtPoll,
    DataRequestOptions,
    PollListResponse,
    PollResponse,
    PollResultsResponse,
    QuestionExt,
    QuestionsResponse,
    ResponsesNum,
    ScriptQuestSets,
    Scripts
} from "../../models";
import {CommonUtils} from "../utils/common.utils";

@Injectable()
export class AuthorDataService extends BasicDataService {

    constructor(@Self() protected http: HttpClient,
                protected locationStrategy: LocationStrategy,
                protected logger: LoggerService) {
        super(http, locationStrategy, logger);
    }

    public authorInsertBtPoll(poll: BtPoll, isPublic: boolean, options: DataRequestOptions): Observable<AuthorEntirePollResponse> {
        this.logger.log('AuthorDataService: authorInsertBtPoll');

        const url = this.POLL_SERVICE_URL + 'author/poll/insert.json' + (isPublic ? '?isPublic=true' : '');

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<AuthorEntirePollResponse>(url, {record: poll}, {headers: headers});
    }


    public getAuthorEntirePoll(pollId: number, options: DataRequestOptions): Observable<AuthorEntirePollResponse> {
        this.logger.log('AuthorDataService: getAuthorEntirePoll', pollId);

        const url = this.POLL_SERVICE_URL + 'author/poll/' + pollId + '/entire.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.get<AuthorEntirePollResponse>(url, {headers: headers});
    }

    public changePollAudience(pollId: number, makePublic: boolean, options: DataRequestOptions): Observable<AuthorEntirePollResponse> {
        this.logger.log('AuthorDataService: changePollAudience', pollId);

        const url = this.POLL_SERVICE_URL + 'author/poll/' + pollId + '/changeAudience.json?makePublic=' + makePublic;

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<AuthorEntirePollResponse>(url, null, {headers: headers});
    }

    public getAuthorPoll(pollId: number, options: DataRequestOptions): Observable<AuthorPollResponse> {
        this.logger.log('AuthorDataService: getAuthorPoll', pollId);

        const url = this.POLL_SERVICE_URL + 'author/poll/' + pollId + '/get.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.get<AuthorPollResponse>(url, {headers: headers});
    }

    public getResponsesNum(pollId: number, options: DataRequestOptions): Observable<ResponsesNum> {
        this.logger.log('AuthorDataService: getResponsesNum', pollId);

        const url = this.POLL_SERVICE_URL + 'author/poll/' + pollId + '/responsesNum.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.get<ResponsesNum>(url, {headers: headers});
    }

    public publishPoll(pollId: number, options: DataRequestOptions): Observable<number> {
        this.logger.log('AuthorDataService: publishPoll', pollId);

        const url = this.POLL_SERVICE_URL + 'author/publish/' + pollId + '.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<number>(url, null, {headers: headers});

    }

    public unpublishPoll(pollId: number, options: DataRequestOptions): Observable<number> {
        this.logger.log('AuthorDataService: unpublishPoll', pollId);

        const url = this.POLL_SERVICE_URL + 'author/unpublish/' + pollId + '.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<number>(url, null, {headers: headers});

    }

    public emailResponsesToMe(pollId: number, options: DataRequestOptions): Observable<string> {

        this.logger.log('AuthorDataService: emailResponsesToMe', pollId);

        const url = this.POLL_SERVICE_URL + 'author/results/' + pollId + '/email.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<string>(url, null, {headers: headers});
    }


    public updateAuthorPoll(poll: BtPoll, pollId: number, updateAllFields: boolean, options: DataRequestOptions): Observable<PollResponse> {
        this.logger.log('AuthorDataService: updateAuthorPoll', poll, pollId);

        const url = this.POLL_SERVICE_URL + 'author/poll/' + pollId + '/update.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        let params = new HttpParams();
        if (!CommonUtils.isNullOrUndefined(updateAllFields)) {
            params = params.append('updateAllFields', String(updateAllFields));
        }

        return this.http.post<PollResponse>(url, {record: poll}, {headers: headers, params: params});
    }

    public deletePoll(pollId: number, options: DataRequestOptions): Observable<PollResponse> {
        this.logger.log('AuthorDataService: deletePoll', pollId);

        const url = this.POLL_SERVICE_URL + 'author/poll/' + pollId + '/delete.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<PollResponse>(url, null, {headers: headers});
    }

    public deletePollResults(pollId: number, options: DataRequestOptions): Observable<string> {
        this.logger.log('AuthorDataService: deletePollResults', pollId);

        const url = this.POLL_SERVICE_URL + 'author/results/' + pollId + '/delete.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<string>(url, null, {headers: headers});
    }


    public copyPoll(pollId: number, newTitle: string, options: DataRequestOptions) {
        this.logger.log('AuthorDataService: copyPoll', pollId, newTitle);

        const url = this.POLL_SERVICE_URL + 'author/poll/' + pollId + '/copy.json';

        let params = new HttpParams();
        params = params.append('title', String(newTitle));

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<PollListResponse>(url, null, {headers: headers, params: params});

    }


    public updateAuthorPollQuestions(questions: QuestionExt[], pollId: number, options: DataRequestOptions): Observable<QuestionsResponse> {
        this.logger.log('AuthorDataService: updateAuthorPollQuestion', pollId);

        const url = this.POLL_SERVICE_URL + 'author/questions/' + pollId + '/update.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<QuestionsResponse>(url, {records: questions}, {headers: headers});
    }

    public replaceAllQuestionsAndUpdateScriptQuestSets(questions: QuestionExt[], scriptQuestSets: any,
                                                       questNumPairs: any[], keepResponses: boolean, pollId: number, options: DataRequestOptions): Observable<QuestionsResponse> {
        this.logger.log('AuthorDataService: replaceAllQuestionsAndUpdateScriptQuestSets', pollId);

        const url = this.POLL_SERVICE_URL + 'author/questions/' + pollId + '/replaceExt.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);
        const payload = {questionsUpdate: {records: questions}, responsesUpdate: {questNumPairs: questNumPairs, keepResponses: keepResponses}};
        if (scriptQuestSets) {
            payload['scriptQuestSetsUpdate'] = {records: scriptQuestSets};
        }
        return this.http.post<QuestionsResponse>(url, payload, {headers: headers});
    }

    public deleteAuthorPollQuestions(questNums: number[], pollId: number, options: DataRequestOptions): Observable<QuestionsResponse> {
        this.logger.log('AuthorDataService: deleteAuthorPollQuestions', questNums, pollId);

        const url = this.POLL_SERVICE_URL + 'author/questions/' + pollId + '/delete.json';

        let params = new HttpParams();
        params = params.append('questSetDesc', questNums.join(','));

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<QuestionsResponse>(url, {}, {headers: headers, params: params});

    }

    public replaceAllScriptQuestSets(scriptQuestSets: ScriptQuestSets, questNumPairs: any[], keepResponses: boolean,
                                     pollId: number | any, options: DataRequestOptions) {
        this.logger.log('AuthorDataService: replaceAllScriptQuestSets', pollId);

        const url = this.POLL_SERVICE_URL + 'author/scriptQuestSets/' + pollId + '/replaceAll.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<any>(url, {
            records: scriptQuestSets,
            responsesUpdate: {
                questNumPairs: questNumPairs,
                keepResponses: keepResponses
            }
        }, {headers: headers});

    }

    public replaceAllScriptsAndScriptQuestSets(scripts: Scripts, scriptQuestSets: ScriptQuestSets,
                                               questNumPairs: any[], keepResponses: boolean, pollId: number | any, options: DataRequestOptions) {
        this.logger.log('AuthorDataService: replaceAllScriptsAndScriptQuestSets', pollId);

        const url = this.POLL_SERVICE_URL + 'author/scripts/' + pollId + '/replaceExt.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<any>(url,
            {
                scripts: scripts,
                scriptQuestSets: scriptQuestSets,
                responsesUpdate: {
                    questNumPairs: questNumPairs,
                    keepResponses: keepResponses
                }
            }, {headers: headers});

    }

    public getAuthorPollsList(queryParams: {}, options: DataRequestOptions) {
        this.logger.log('AuthorDataService: getAuthorPollsList', queryParams);

        const url = this.POLL_SERVICE_URL + 'author/poll/list.json';

        let params = new HttpParams();
        Object.keys(queryParams).forEach(key => params = params.append(key, queryParams[key]));

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.get<PollListResponse>(url, {headers: headers, params: params});

    }

    public getAuthorTakerGroups(pollId: number, options: DataRequestOptions): Observable<any> {
        this.logger.log('AuthorDataService: getAuthorTargetGroups', pollId);

        const url = this.POLL_SERVICE_URL + 'author/takerGroups/' + pollId + '/get.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.get<any>(url, {headers: headers});
    }

    public getAuthorAuthorGroups(pollId: number, options: DataRequestOptions): Observable<any> {
        this.logger.log('AuthorDataService: getAuthorAuthorGroups', pollId);

        const url = this.POLL_SERVICE_URL + 'author/authorGroups/' + pollId + '/get.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.get<any>(url, {headers: headers});
    }

    public updateTakerGroups(pollId: number, obj: any, options: DataRequestOptions): Observable<any> {
        this.logger.log('AuthorDataService: updateTakerGroups', pollId);

        const url = this.POLL_SERVICE_URL + 'author/takerGroups/' + pollId + '/update.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<any>(url, obj, {headers: headers});

    }

    public updateAuthorGroups(pollId: number, obj: any, options: DataRequestOptions): Observable<any> {
        this.logger.log('AuthorDataService: updateAuthorGroups', pollId);

        const url = this.POLL_SERVICE_URL + 'author/authorGroups/' + pollId + '/update.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<any>(url, obj, {headers: headers});

    }

    public deleteTakerGroups(pollId: number, obj: any, options: DataRequestOptions): Observable<any> {
        this.logger.log('AuthorDataService: deleteTakerGroups', pollId);

        const url = this.POLL_SERVICE_URL + 'author/takerGroups/' + pollId + '/delete.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<any>(url, obj, {headers: headers});

    }

    public deleteAuthorGroups(pollId: number, obj: any, options: DataRequestOptions): Observable<any> {
        this.logger.log('AuthorDataService: deleteAuthorGroups', pollId);

        const url = this.POLL_SERVICE_URL + 'author/authorGroups/' + pollId + '/delete.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.post<any>(url, obj, {headers: headers});

    }

    public getAuthorPollResults(pollId: number, options: DataRequestOptions): Observable<PollResultsResponse> {

        this.logger.log('AuthorDataService: getAuthorPollResults', pollId, options.anonymous);

        const url = this.POLL_SERVICE_URL + 'author/results/' + pollId + '/get.json';

        const headers: HttpHeaders = this.dataRequestOptionsToHeaders(options);

        return this.http.get<PollResultsResponse>(url, {headers: headers});
    }

}
